import { useEffect, useRef, useState, type ReactNode } from 'react';

type Props = {
  children: ReactNode;
  placeholderHeight: number;
  rootMargin?: string;
};

export default function LazyMount({
  children,
  placeholderHeight,
  rootMargin = '200px',
}: Props) {
  const [mounted, setMounted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (mounted) return;
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setMounted(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setMounted(true);
            observer.disconnect();
            return;
          }
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [mounted, rootMargin]);

  if (mounted) return <>{children}</>;
  return (
    <div
      ref={ref}
      style={{
        height: placeholderHeight,
        width: '100%',
        background: 'var(--color-kumo)',
        borderRadius: 'var(--radius-md)',
        border: 'var(--border-medium)',
      }}
      aria-hidden
    />
  );
}
